import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';

// ============================================================================
// LANDING PAGE - CO-STAR FRAMEWORK IMPLEMENTATION
// ============================================================================
// Context: Companies facing rising legal scrutiny over digital accessibility
// Objective: Informative conversion—educate on risk, then provide the solution
// Style/Tone: Professional but approachable, McKinsey-style strategic clarity
// Audience: CFOs and legal teams who prioritize risk mitigation and ROI
// ============================================================================

// Animated counter hook for statistics
const useCountUp = (end, duration = 2000, startOnView = true) => {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!startOnView) {
      setHasStarted(true);
    }
  }, [startOnView]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;

    let startTime;
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    requestAnimationFrame(animate);
  }, [hasStarted, end, duration]);

  return { count, ref };
};

// Shield Icon Component
const ShieldIcon = ({ className = "w-8 h-8" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </svg>
);

// Chart Icon Component
const ChartIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

// Scale/Legal Icon Component
const ScaleIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
  </svg>
);

// Scan Icon Component
const ScanIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
  </svg>
);

// Bolt Icon Component
const BoltIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
  </svg>
);

// Document Icon Component
const DocumentIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

// Clock Icon Component
const ClockIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Check Circle Icon
const CheckCircleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// Arrow Right Icon
const ArrowRightIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
  </svg>
);

const LandingPage = () => {
  // Statistics with animated counters (Chain of Density - real data points)
  const lawsuitStat = useCountUp(4605, 2000);
  const wcagViolations = useCountUp(959, 2000); // 95.9% displayed as counter

  useEffect(() => {
    // Track page view for analytics
  }, []);

  return (
    <div className="min-h-screen bg-background text-white overflow-hidden relative">
      {/* Background Mesh Gradient */}
      <div className="fixed inset-0 bg-gradient-mesh pointer-events-none" />

      {/* Animated Background Orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[-20%] left-[-10%] w-[50%] h-[50%] bg-electric-500/8 rounded-full blur-[120px] animate-pulse-slow" />
        <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-primary-500/8 rounded-full blur-[120px] animate-pulse-slow" style={{ animationDelay: '2s' }} />
        <div className="absolute top-[40%] right-[20%] w-[30%] h-[30%] bg-secondary-500/5 rounded-full blur-[100px]" />
      </div>

      {/* ================================================================== */}
      {/* HERO SECTION - BAB Framework (Before-After-Bridge)                */}
      {/* ================================================================== */}
      <section className="relative z-10 pt-28 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-16">

            {/* Hero Text Content */}
            <div className="w-full lg:w-1/2 text-center lg:text-left space-y-8">
              {/* Status Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-electric-500/30 bg-electric-500/10 backdrop-blur-md">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-electric-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-electric-400"></span>
                </span>
                <span className="text-electric-300 font-medium text-sm">WCAG 2.2 AA Compliant Engine</span>
              </div>

              {/* BEFORE: The Pain Point */}
              <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold font-heading leading-[1.1] tracking-tight">
                Stop ADA Lawsuits <br />
                <span className="text-gradient-electric">Before They Start</span>
              </h1>

              {/* AFTER: The Vision + BRIDGE: The Solution */}
              <p className="text-lg sm:text-xl text-slate-400 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Achieve continuous WCAG 2.2 compliance with our AI-powered platform.
                Identify violations, generate remediation code, and build
                audit-ready documentation that stands up in court.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start pt-4">
                <Link
                  to="/subscription"
                  className="group relative inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-primary-600 to-electric-600 rounded-xl font-semibold text-white shadow-glow hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <span>View Pricing Plans</span>
                  <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-white border border-white/10 hover:bg-white/5 transition-all duration-300 hover:border-white/20 backdrop-blur-sm"
                >
                  <span>Get Started</span>
                  <ArrowRightIcon className="w-5 h-5" />
                </Link>
              </div>

              {/* Trust Signals */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-6 gap-y-2 pt-4 text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-electric-400" />
                  5-minute setup
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-electric-400" />
                  WCAG 2.2 AA compliant
                </span>
                <span className="flex items-center gap-1.5">
                  <CheckCircleIcon className="w-4 h-4 text-electric-400" />
                  Court-ready documentation
                </span>
              </div>
            </div>

            {/* Hero Visual - Dashboard Wireframe Preview */}
            <div className="w-full lg:w-1/2 relative">
              <DashboardPreview />
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider" />

      {/* ================================================================== */}
      {/* RISK STATISTICS SECTION - Chain of Density Content                */}
      {/* Real lawsuit data, regulatory references (ADA, WCAG 2.2)          */}
      {/* ================================================================== */}
      <section className="relative z-10 py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-danger-500/10 text-danger-400 border border-danger-500/20 mb-4">
              Current Legal Landscape
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading mb-6">
              Digital Accessibility Lawsuits<br />
              <span className="text-gradient">Continue to Rise</span>
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed">
              Under Title III of the Americans with Disabilities Act (ADA), businesses face escalating
              legal action for inaccessible digital properties. The DOJ's 2024 web accessibility rule
              mandates WCAG 2.1 Level AA compliance for state and local government entities.
            </p>
          </div>

          {/* Statistics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {/* Stat Card 1 - Lawsuits Filed */}
            <div ref={lawsuitStat.ref} className="dashboard-card p-8 text-center group hover:border-danger-500/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-danger-500/10 border border-danger-500/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <ScaleIcon className="w-7 h-7 text-danger-400" />
              </div>
              <div className="stat-number text-5xl lg:text-6xl font-bold text-white mb-2">
                {lawsuitStat.count.toLocaleString()}
              </div>
              <div className="text-slate-400 font-medium mb-2">ADA Lawsuits Filed (2023)</div>
              <div className="text-xs text-slate-500">
                Source: UsableNet Year-End Report
              </div>
            </div>

            {/* Stat Card 2 - Settlement Range */}
            <div className="dashboard-card p-8 text-center group hover:border-warning-500/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-warning-500/10 border border-warning-500/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <ChartIcon className="w-7 h-7 text-warning-400" />
              </div>
              <div className="stat-number text-5xl lg:text-6xl font-bold text-white mb-2">
                $30-100K
              </div>
              <div className="text-slate-400 font-medium mb-2">Typical Settlement Range</div>
              <div className="text-xs text-slate-500">
                Plus legal fees ($10K-$150K additional)
              </div>
            </div>

            {/* Stat Card 3 - Violation Rate */}
            <div ref={wcagViolations.ref} className="dashboard-card p-8 text-center group hover:border-electric-500/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-electric-500/10 border border-electric-500/20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
                <ScanIcon className="w-7 h-7 text-electric-400" />
              </div>
              <div className="stat-number text-5xl lg:text-6xl font-bold text-white mb-2">
                {(wcagViolations.count / 10).toFixed(1)}%
              </div>
              <div className="text-slate-400 font-medium mb-2">Of Sites Have WCAG Errors</div>
              <div className="text-xs text-slate-500">
                WebAIM Million 2024 Analysis
              </div>
            </div>
          </div>

          {/* Risk Alert Banner */}
          <div className="mt-12 dashboard-card p-6 border-l-4 border-l-warning-500">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-warning-500/10 flex items-center justify-center">
                <svg className="w-5 h-5 text-warning-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="flex-1">
                <h4 className="text-white font-semibold mb-1">Serial Plaintiff Risk Assessment</h4>
                <p className="text-slate-400 text-sm">
                  A single law firm filed 1,124 ADA digital lawsuits in 2024. Serial plaintiffs use automated
                  scanning tools to identify targets. AccessShield's continuous monitoring ensures you're never
                  caught off-guard.
                </p>
              </div>
              <Link to="/subscription" className="flex-shrink-0 px-4 py-2 bg-warning-500/10 border border-warning-500/30 rounded-lg text-warning-400 text-sm font-medium hover:bg-warning-500/20 transition-colors">
                Get Protected Now
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider" />

      {/* ================================================================== */}
      {/* SOLUTION SECTION - Feature Grid                                    */}
      {/* ================================================================== */}
      <section className="relative z-10 py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-transparent via-surface/50 to-transparent">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-electric-500/10 text-electric-400 border border-electric-500/20 mb-4">
              Enterprise Platform
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading mb-6">
              Automated Compliance.<br />
              <span className="text-gradient-cyan">Documented Protection.</span>
            </h2>
            <p className="text-slate-400 text-lg leading-relaxed">
              AccessShield combines AI-powered scanning, automated remediation, and audit-ready
              documentation into a single platform designed for enterprise legal and compliance teams.
            </p>
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {/* Feature 1 - AI Audit Engine */}
            <div className="dashboard-card p-8 group hover:border-primary-500/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary-500 to-electric-500 flex items-center justify-center mb-6 shadow-lg shadow-primary-500/20 group-hover:scale-110 transition-transform">
                <ScanIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">AI Audit Engine</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                Scans 10,000+ pages in under 5 minutes. Identifies WCAG 2.2 violations with 99.2%
                accuracy—detecting 30% more issues than traditional automated tools.
              </p>
              <ul className="space-y-2 text-sm text-slate-500">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Full WCAG 2.2 Level AA coverage
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Dynamic content testing
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Mobile & cross-browser validation
                </li>
              </ul>
            </div>

            {/* Feature 2 - Smart Remediation */}
            <div className="dashboard-card p-8 group hover:border-secondary-500/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-secondary-500 to-pink-500 flex items-center justify-center mb-6 shadow-lg shadow-secondary-500/20 group-hover:scale-110 transition-transform">
                <BoltIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Smart Remediation</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                AI-generated code fixes for 85% of violations. One-click deployment or export to
                your dev workflow. Average time-to-compliance reduced from 6 weeks to 72 hours.
              </p>
              <ul className="space-y-2 text-sm text-slate-500">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Auto-generated alt text & ARIA labels
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Color contrast auto-correction
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Keyboard navigation fixes
                </li>
              </ul>
            </div>

            {/* Feature 3 - Legal Documentation */}
            <div className="dashboard-card p-8 group hover:border-success-500/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-success-500 to-electric-500 flex items-center justify-center mb-6 shadow-lg shadow-success-500/20 group-hover:scale-110 transition-transform">
                <DocumentIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Legal Documentation</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                Generate court-admissible compliance reports with timestamped audit trails.
                VPAT/ACR documentation included. Designed with accessibility litigation defense counsel.
              </p>
              <ul className="space-y-2 text-sm text-slate-500">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Automated VPAT generation
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Timestamped remediation proof
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Export for legal proceedings
                </li>
              </ul>
            </div>

            {/* Feature 4 - Continuous Monitoring */}
            <div className="dashboard-card p-8 group hover:border-electric-500/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-electric-500 to-primary-500 flex items-center justify-center mb-6 shadow-lg shadow-electric-500/20 group-hover:scale-110 transition-transform">
                <ClockIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">24/7 Monitoring</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                Real-time alerts when new violations are introduced. Integrates with CI/CD pipelines
                to catch issues before deployment. Never be blindsided by a demand letter again.
              </p>
              <ul className="space-y-2 text-sm text-slate-500">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Slack, Teams & email alerts
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  GitHub/GitLab integration
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Pre-deployment gates
                </li>
              </ul>
            </div>

            {/* Feature 5 - Risk Dashboard */}
            <div className="dashboard-card p-8 group hover:border-warning-500/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-warning-500 to-orange-500 flex items-center justify-center mb-6 shadow-lg shadow-warning-500/20 group-hover:scale-110 transition-transform">
                <ChartIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Executive Risk Dashboard</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                Board-ready reports with litigation risk scores, compliance trends, and ROI metrics.
                Track progress across multiple properties from a single pane of glass.
              </p>
              <ul className="space-y-2 text-sm text-slate-500">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Multi-domain portfolio view
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Custom compliance KPIs
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Scheduled executive summaries
                </li>
              </ul>
            </div>

            {/* Feature 6 - Enterprise Security */}
            <div className="dashboard-card p-8 group hover:border-slate-500/30 transition-all duration-300">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center mb-6 shadow-lg shadow-slate-500/20 group-hover:scale-110 transition-transform">
                <ShieldIcon className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Enterprise Security</h3>
              <p className="text-slate-400 leading-relaxed mb-4">
                SOC 2 Type II certified. GDPR compliant. Single sign-on with SAML 2.0.
                Your audit data never leaves your approved regions.
              </p>
              <ul className="space-y-2 text-sm text-slate-500">
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  SSO & SCIM provisioning
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Role-based access control
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircleIcon className="w-4 h-4 text-success-400" />
                  Data residency options
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider" />

      {/* ================================================================== */}
      {/* WHY ACCESSSHIELD SECTION                                          */}
      {/* ================================================================== */}
      <section className="relative z-10 py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Value Proposition */}
          <div className="max-w-4xl mx-auto">
            <div className="dashboard-card p-8 lg:p-12 relative overflow-hidden">
              <div className="relative z-10 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-electric-500 flex items-center justify-center mx-auto mb-6 shadow-lg shadow-primary-500/20">
                  <ShieldIcon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl lg:text-3xl font-bold text-white mb-4">
                  Proactive Compliance, Not Reactive Damage Control
                </h3>
                <p className="text-lg text-slate-400 leading-relaxed max-w-2xl mx-auto">
                  Most businesses discover accessibility issues when they receive a demand letter.
                  AccessShield identifies and remediates violations before plaintiffs do—giving you
                  documented proof of compliance that stands up in court.
                </p>
              </div>
            </div>
          </div>

          {/* Platform Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
            {[
              { value: '99.2%', label: 'Detection Accuracy' },
              { value: '85%', label: 'Auto-Remediation Rate' },
              { value: '<5min', label: 'Full Site Scan' },
              { value: '24/7', label: 'Continuous Monitoring' }
            ].map((metric, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl lg:text-4xl font-bold text-white mb-1">{metric.value}</div>
                <div className="text-slate-500 text-sm">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider" />

      {/* ================================================================== */}
      {/* FINAL CTA SECTION                                                  */}
      {/* ================================================================== */}
      <section className="relative z-10 py-24 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-heading mb-6">
            Know Your Risk.<br />
            <span className="text-gradient-electric">Fix It Before Plaintiffs Find It.</span>
          </h2>
          <p className="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">
            Get a comprehensive WCAG 2.2 compliance audit with detailed violation reports,
            prioritized remediation steps, and legal documentation—all in under 5 minutes.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/subscription"
              className="px-8 py-4 bg-gradient-to-r from-primary-600 to-electric-600 rounded-xl font-semibold text-white shadow-glow hover:shadow-glow-lg transition-all duration-300 hover:-translate-y-0.5"
            >
              View Pricing Plans
            </Link>
            <Link
              to="/signup"
              className="px-8 py-4 rounded-xl font-medium text-white border border-white/10 hover:bg-white/5 transition-all duration-300 hover:border-white/20 backdrop-blur-sm"
            >
              Get Started Today
            </Link>
          </div>

          {/* Compliance Badges */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-12">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface border border-white/5">
              <ShieldIcon className="w-5 h-5 text-electric-400" />
              <span className="text-sm text-slate-400">SOC 2 Type II</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface border border-white/5">
              <ShieldIcon className="w-5 h-5 text-electric-400" />
              <span className="text-sm text-slate-400">GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-surface border border-white/5">
              <ShieldIcon className="w-5 h-5 text-electric-400" />
              <span className="text-sm text-slate-400">WCAG 2.2 AA</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer spacing */}
      <div className="h-20" />
    </div>
  );
};

// ============================================================================
// DASHBOARD PREVIEW COMPONENT
// Modular cards showing audit scores and legal risk levels
// ============================================================================
const DashboardPreview = () => {
  return (
    <div className="relative w-full max-w-lg mx-auto">
      {/* Rotating Rings Background */}
      <div className="absolute inset-0 flex items-center justify-center z-0">
        <div className="w-[450px] h-[450px] border border-white/5 rounded-full animate-[spin_20s_linear_infinite]" />
        <div className="w-[350px] h-[350px] border border-white/5 rounded-full absolute animate-[spin_15s_linear_infinite_reverse]" />
        <div className="w-[250px] h-[250px] border border-electric-500/10 rounded-full absolute animate-[spin_10s_linear_infinite]" />
      </div>

      {/* Main Dashboard Card */}
      <div className="relative z-10 animate-float">
        <div className="dashboard-card p-6 rounded-2xl w-full">
          {/* Header Bar */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-electric-500 flex items-center justify-center">
                <ShieldIcon className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-white font-semibold text-sm">AccessShield</div>
                <div className="text-slate-500 text-xs">Compliance Dashboard</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-success-400 animate-pulse"></span>
              <span className="text-xs text-slate-400">Live</span>
            </div>
          </div>

          {/* Compliance Score */}
          <div className="mb-6">
            <div className="flex items-end justify-between mb-2">
              <span className="text-slate-400 text-sm">Overall Compliance</span>
              <span className="text-success-400 text-xs font-medium">+12% this week</span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-5xl font-bold text-white">94</span>
              <span className="text-slate-500 text-xl">/100</span>
            </div>
            {/* Progress Bar */}
            <div className="mt-3 h-2 bg-slate-800 rounded-full overflow-hidden">
              <div className="h-full w-[94%] bg-gradient-to-r from-success-500 to-electric-500 rounded-full" />
            </div>
          </div>

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-surface/50 rounded-xl p-3">
              <div className="text-slate-500 text-xs mb-1">Pages Scanned</div>
              <div className="text-white font-semibold">2,847</div>
            </div>
            <div className="bg-surface/50 rounded-xl p-3">
              <div className="text-slate-500 text-xs mb-1">Issues Fixed</div>
              <div className="text-white font-semibold">1,203</div>
            </div>
            <div className="bg-surface/50 rounded-xl p-3">
              <div className="text-slate-500 text-xs mb-1">Risk Level</div>
              <div className="text-success-400 font-semibold">Low</div>
            </div>
            <div className="bg-surface/50 rounded-xl p-3">
              <div className="text-slate-500 text-xs mb-1">Last Scan</div>
              <div className="text-white font-semibold">2m ago</div>
            </div>
          </div>

          {/* Recent Issues List */}
          <div className="space-y-2">
            <div className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">Recent Fixes</div>
            {[
              { issue: 'Missing alt text on images', status: 'fixed', count: 47 },
              { issue: 'Low color contrast', status: 'fixed', count: 23 },
              { issue: 'Form labels missing', status: 'in_progress', count: 8 }
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2 px-3 bg-surface/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className={`w-1.5 h-1.5 rounded-full ${item.status === 'fixed' ? 'bg-success-400' : 'bg-warning-400'}`} />
                  <span className="text-slate-300 text-sm">{item.issue}</span>
                </div>
                <span className="text-slate-500 text-xs">{item.count} items</span>
              </div>
            ))}
          </div>

          {/* Scanning Effect */}
          <div className="absolute left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-electric-400/50 to-transparent top-[30%] animate-[scan_4s_linear_infinite]" />
        </div>

        {/* Floating Side Card - Risk Alert */}
        <div className="absolute -right-4 top-1/4 glass-panel p-4 rounded-xl animate-float" style={{ animationDelay: '1s' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-success-500/20 flex items-center justify-center">
              <CheckCircleIcon className="w-4 h-4 text-success-400" />
            </div>
            <div>
              <div className="text-xs text-slate-400">Legal Risk</div>
              <div className="text-sm font-semibold text-success-400">Protected</div>
            </div>
          </div>
        </div>

        {/* Floating Side Card - Compliance Badge */}
        <div className="absolute -left-4 bottom-1/4 glass-panel p-3 rounded-xl animate-float" style={{ animationDelay: '2s' }}>
          <div className="flex items-center gap-2">
            <span className="text-electric-400 text-xs font-mono">WCAG 2.2 AA</span>
            <CheckCircleIcon className="w-4 h-4 text-electric-400" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;